import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Loader2, RefreshCw, RotateCcw, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { OrderDetailsModal } from '@/components/OrderDetailsModal';
import { APP_BASE } from '@/lib/api';

const API = `${APP_BASE}/api`;

const STATUS_LABELS = {
  pending: 'In attesa',
  paid: 'Pagato',
  cancelled: 'Annullato',
  preparing: 'In preparazione',
  ready: 'Pronto',
  completed: 'Completato',
};

const formatDateTime = (value) => {
  if (!value) return 'N/D';

  try {
    return new Intl.DateTimeFormat('it-IT', {
      timeZone: 'Europe/Rome',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const buildOrderStart = (order) => {
  if (order?.pickup_datetime) return order.pickup_datetime;
  if (order?.pickup_date && order?.pickup_time) {
    return `${order.pickup_date}T${order.pickup_time}:00`;
  }
  return order?.created_at || '';
};

export default function AdminOrdersTrash() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const fetchTrashOrders = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/admin/orders.php?scope=trash`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error('Errore caricamento cestino ordini');
      }

      const data = await response.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching trash orders:', error);
      toast.error(error?.message || 'Errore nel caricamento del cestino');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTrashOrders();
  }, [fetchTrashOrders]);

  const handleRestore = async (orderId) => {
    if (!token) {
      toast.error('Sessione scaduta, rifai login');
      return;
    }

    setRestoringId(orderId);

    try {
      const response = await fetch(`${API}/orders/restore.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ order_id: orderId }),
      });

      if (!response.ok) {
        let message = 'Errore ripristino ordine';
        try {
          const data = await response.json();
          message = data?.detail || data?.error || message;
        } catch {}
        throw new Error(message);
      }

      setOrders((prev) => prev.filter((order) => order.id !== orderId));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(null);
        setIsDetailsOpen(false);
      }
      toast.success('Ordine ripristinato nel calendario');
    } catch (error) {
      console.error('Error restoring order:', error);
      toast.error(error?.message || 'Errore durante il ripristino');
    } finally {
      setRestoringId(null);
    }
  };

  const stats = useMemo(() => {
    return {
      total: orders.length,
      pickup: orders.filter((order) => order?.fulfillment_type === 'PICKUP').length,
      delivery: orders.filter((order) => order?.fulfillment_type === 'DELIVERY').length,
    };
  }, [orders]);

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Cestino Ordini</h1>
              <p className="text-xs text-muted-foreground">Ordini rimossi dal calendario admin</p>
            </div>
          </div>

          <Button variant="outline" size="sm" onClick={fetchTrashOrders} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="text-2xl font-bold text-foreground">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Ordini nel cestino</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-success">{stats.pickup}</div>
            <div className="text-sm text-muted-foreground">Ritiri rimossi</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-instagram-purple">{stats.delivery}</div>
            <div className="text-sm text-muted-foreground">Consegne rimosse</div>
          </Card>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-instagram-purple" />
          </div>
        ) : orders.length === 0 ? (
          <Card className="p-10 text-center">
            <Trash2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Il cestino ordini è vuoto.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => {
              const startValue = buildOrderStart(order);
              const statusLabel = STATUS_LABELS[order?.status] || order?.status || 'N/D';

              return (
                <Card key={order.id} className="border-destructive/20">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="border-destructive text-destructive">
                            Eliminato dal calendario
                          </Badge>
                          <Badge variant="secondary">
                            {order?.fulfillment_type === 'DELIVERY' ? 'Consegna' : 'Ritiro in sede'}
                          </Badge>
                          <Badge variant="outline">{statusLabel}</Badge>
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-foreground">
                            Ordine #{order.id} - {order.customer_name || 'Cliente'}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {formatDateTime(startValue)} • Totale €{Number(order.total || 0).toFixed(2)}
                          </p>
                        </div>

                        <div className="text-sm text-muted-foreground">
                          {order.customer_phone || 'Telefono non disponibile'}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedOrder(order);
                            setIsDetailsOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Dettagli
                        </Button>

                        <Button
                          variant="default"
                          className="gradient-instagram text-primary-foreground"
                          onClick={() => handleRestore(order.id)}
                          disabled={restoringId === order.id}
                        >
                          {restoringId === order.id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Ripristino...
                            </>
                          ) : (
                            <>
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Ripristina
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <OrderDetailsModal
        orderId={selectedOrder?.id || null}
        orderData={selectedOrder || null}
        open={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedOrder(null);
        }}
        token={token}
        showManagementActions={false}
      />
    </div>
  );
}
